import { supabase } from './client';
import { getCurrentEmpresaId } from './auth-utils';

export interface ContaBancaria {
  id?: string;
  empresa_id?: string;
  nome: string;
  tipo: 'caixa' | 'banco' | 'carteira' | 'marketplace' | 'outro';
  banco?: string;
  numero_conta?: string;
  agencia?: string;
  titular?: string;
  saldo_inicial: number;
  data_saldo_inicial: string;
  ativa: boolean;
  cor?: string;
  ícone?: string;
  created_at?: string;
  updated_at?: string;
}

export async function listarContasBancarias(): Promise<ContaBancaria[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data, error } = await supabase
      .from('contas_bancarias')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('ativa', true)
      .order('nome', { ascending: true });

    if (error) {
      console.error('Erro ao listar contas bancárias:', error);
      return [];
    }

    return (data || []) as ContaBancaria[];
  } catch (erro) {
    console.error('Erro ao listar contas bancárias:', erro);
    return [];
  }
}

export async function criarContaBancaria(conta: Omit<ContaBancaria, 'id' | 'created_at' | 'updated_at' | 'empresa_id'>): Promise<{ ok: boolean; data?: ContaBancaria; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    const { data, error } = await supabase
      .from('contas_bancarias')
      .insert({
        ...conta,
        empresa_id: empresaId,
        ativa: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar conta bancária:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true, data: data as ContaBancaria };
  } catch (erro: any) {
    console.error('Erro ao criar conta bancária:', erro);
    return { ok: false, error: erro.message || 'Erro ao criar conta bancária' };
  }
}

export async function atualizarContaBancaria(id: string, conta: Partial<ContaBancaria>): Promise<{ ok: boolean; data?: ContaBancaria; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    const { data, error } = await supabase
      .from('contas_bancarias')
      .update({
        ...conta,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('empresa_id', empresaId)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar conta bancária:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true, data: data as ContaBancaria };
  } catch (erro: any) {
    console.error('Erro ao atualizar conta bancária:', erro);
    return { ok: false, error: erro.message || 'Erro ao atualizar conta bancária' };
  }
}

export async function deletarContaBancaria(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    const { error } = await supabase
      .from('contas_bancarias')
      .update({ ativa: false, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('empresa_id', empresaId);

    if (error) {
      console.error('Erro ao deletar conta bancária:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true };
  } catch (erro: any) {
    console.error('Erro ao deletar conta bancária:', erro);
    return { ok: false, error: erro.message || 'Erro ao deletar conta bancária' };
  }
}

/**
 * Calcula o saldo total de uma conta baseado em todas as transações
 */
export async function calcularSaldoConta(contaId: string): Promise<number> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return 0;
    }

    // Buscar a conta
    const { data: conta } = await supabase
      .from('contas_bancarias')
      .select('saldo_inicial')
      .eq('id', contaId)
      .eq('empresa_id', empresaId)
      .single();

    if (!conta) {
      return 0;
    }

    let saldoAtual = conta.saldo_inicial;

    // Buscar movimentações que afetam a conta
    // Isso seria implementado quando tivermos a tabela de movimentações

    return saldoAtual;
  } catch (erro) {
    console.error('Erro ao calcular saldo da conta:', erro);
    return 0;
  }
}
